/**
 * components/platform/residency.tsx
 *
 * SKPLATFORM-UICC-006 C-1 / DB-006 S2 — the data-residency field group, and
 * the state markers that keep it separate from legal jurisdiction.
 *
 * ── THE ONE RULE THIS FILE EXISTS TO ENFORCE (U-D1) ───────────────────────
 *
 * Residency and legal jurisdiction are TWO VISUALLY SEPARATE GROUPS, never one
 * merged "Location" panel. They are different facts with different lifecycles
 * and different consequences: one is immutable and one is not. The visual
 * separation IS the mechanism that stops them being conflated — two geographic
 * columns will be conflated by someone, and a merged panel is an invitation.
 *
 * Both groups carry a state marker, and the CONTRAST is the point: a reader
 * must be able to see that one of these two geographic facts can be changed
 * and the other cannot.
 *
 * ── AND THE CONTROL THAT MUST NOT EXIST (U-D2) ────────────────────────────
 *
 * There is no "change zone" affordance here in any state — not disabled, not
 * greyed, not tooltipped. An ABSENT control communicates immutability. A
 * disabled one reads as "ask someone for permission", which invites a support
 * request for something that is not possible.
 */

import React from 'react';
import { View, Text } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { FS, FW, SP } from '../../utils/platformTokens';
import type { OrgResidency } from '../../schema/platform/types';

/**
 * The small muted marker. Present on BOTH groups — an immutability marker that
 * appears only on the immutable group tells the reader nothing by contrast.
 */
export const StateMarker: React.FC<{ state: 'immutable' | 'editable' }> = ({ state }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <Text
      accessibilityLabel={state === 'immutable' ? 'This value cannot be changed' : 'This value can be changed'}
      style={{ fontSize: FS.xs, color: c.ink4, fontVariant: ['tabular-nums'] }}
    >
      {state === 'immutable' ? '[ immutable ]' : '[ editable ]'}
    </Text>
  );
};

/** A read-only label/value line. Values that a reader might type or compare
 *  render in mono — DB-006 §0: "mono is load-bearing here". */
const ReadRow: React.FC<{
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  /** Shown instead of hiding the row. An absent row reads as a rendering
   *  fault; "not yet deployed" reads as information. */
  fallback?: string;
  marker?: React.ReactNode;
}> = ({ label, value, mono, fallback = '—', marker }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const missing = value === null || value === undefined || value === '';
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
        gap: SP.s4,
      }}
    >
      <Text style={{ fontSize: FS.sm, color: c.ink3, minWidth: 130 }}>{label}</Text>
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: SP.s2 }}>
        <Text
          selectable
          style={{
            flex: 1,
            fontSize: FS.sm,
            color: missing ? c.ink4 : c.ink,
            fontWeight: FW.medium,
            // theme.font.mono — the repo's own mono stack. DB-006 §0: every key
            // id, zone code, audience string and count goes in mono, so a reader
            // can tell at a glance which strings are values they might type or
            // compare and which are prose.
            ...(mono ? { fontFamily: theme.font.mono } : null),
          }}
        >
          {missing ? fallback : value}
        </Text>
        {marker}
      </View>
    </View>
  );
};

/**
 * The Data residency group.
 *
 * Rendered in BOTH consoles (a tenant admin sees their own; Softknack staff see
 * it on any org), because knowing which jurisdiction your customer identity is
 * processed in is not privileged information — it is a fact the operator is
 * entitled to and may be asked about in an audit.
 */
export const DataResidencyGroup: React.FC<{ residency: OrgResidency | null | undefined }> = ({
  residency,
}) => {
  const { theme } = useTheme();
  const c = theme.colors;

  return (
    <View>
      <Text style={{ fontSize: FS.sm, fontWeight: FW.semibold, color: c.ink, marginBottom: 4 }}>
        Data residency
      </Text>
      {!residency ? (
        <Text style={{ fontSize: FS.xs, color: c.ink4, paddingVertical: 8 }}>
          Not available for this organisation.
        </Text>
      ) : (
        <>
          <ReadRow
            label="Zone"
            value={`${residency.zone}  ·  ${residency.zone_name}`}
            mono
            marker={<StateMarker state="immutable" />}
          />
          <ReadRow label="Processing" value={residency.processing_region} mono />
          <ReadRow label="Regime" value={residency.governing_regime} />
          <ReadRow
            label="Identity service"
            value={residency.passport_issuer}
            mono
            // A zone with no Passport deployment yet shows this rather than
            // hiding the row.
            fallback="not yet deployed"
          />
          {/*
            UICC-006 §3.1 — the REPLACEMENT copy.
            The old text ("Locked to India during current rollout — data
            residency") sat on the `country` field and was wrong three ways: it
            conflated tax jurisdiction with data residency, residency is no
            longer India-only, and `country` was never locked. This is the
            corrected sentence, on the correct field, always visible and never
            a tooltip.
          */}
          <Text style={{ fontSize: FS.xs, color: c.ink3, marginTop: 6, lineHeight: 18 }}>
            Set once when the organisation was created. Changing it would mean relocating every
            customer identity record across a jurisdictional boundary.
          </Text>
        </>
      )}
    </View>
  );
};

/**
 * The heading for the legal/tax group that sits BELOW the residency group.
 *
 * A separate export rather than a wrapper, so the two groups cannot be
 * accidentally nested inside one panel by a future edit — the caller has to
 * place them as siblings, which is the arrangement U-D1 requires.
 */
export const LegalAndTaxHeading: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <Text style={{ fontSize: FS.sm, fontWeight: FW.semibold, color: c.ink, marginBottom: 4 }}>
      Legal and tax
    </Text>
  );
};
